'use client';

import { i18nConfig } from '@/i18n/i18nConfig';
import {
  AUTH0_VALIDATION_ERROR_MESSAGE,
  INVALID_ACCESS_TOKEN_MESSAGE,
} from '@/lib/apiError';
import { type AppRouter } from '@/server/api/root';
import { TRPCErrorSchema } from '@/server/api/trpc-error';
import { ErrorToast } from '@/trpc/ErrorToast/ErrorToast';
import {
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import {
  httpBatchLink,
  httpLink,
  loggerLink,
  splitLink,
  TRPCLink,
} from '@trpc/client';
import { createTRPCReact } from '@trpc/react-query';
import { observable } from '@trpc/server/observable';
import { useState } from 'react';
import { toast } from 'sonner';

const createQueryClient = () =>
  new QueryClient({
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: false,
      },
    },
    queryCache: new QueryCache({
      onError: (error) => {
        const parsedError = TRPCErrorSchema.safeParse(error);
        const apiError = parsedError.success
          ? parsedError.data.data.apiError
          : null;
        toast.error(<ErrorToast error={apiError ?? error} />);
      },
    }),
    mutationCache: new MutationCache({
      onError: (error) => {
        const parsedError = TRPCErrorSchema.safeParse(error);
        const apiError = parsedError.success
          ? parsedError.data.data.apiError
          : null;
        toast.error(<ErrorToast error={apiError ?? error} />);
      },
    }),
  });

const redirectToLoginOnInvalidToken: TRPCLink<AppRouter> =
  () =>
  ({ next, op }) =>
    observable((observer) =>
      next(op).subscribe({
        next: (value) => {
          observer.next(value);
        },
        error: (err) => {
          observer.error(err);
          if (
            err?.data?.code === 'UNAUTHORIZED' &&
            [
              INVALID_ACCESS_TOKEN_MESSAGE,
              AUTH0_VALIDATION_ERROR_MESSAGE,
            ].includes(err.message)
          ) {
            (window as Window).location =
              `/api/auth/logout?locale=${window.location.pathname.split('/')[1] ?? i18nConfig.defaultLocale}`;
          }
        },
        complete: () => {
          observer.complete();
        },
      }),
    );

let clientQueryClientSingleton: QueryClient | undefined = undefined;
const getQueryClient = () => {
  if (typeof window === 'undefined') {
    // Server: always make a new query client
    return createQueryClient();
  }
  // Browser: use singleton pattern to keep the same query client
  return (clientQueryClientSingleton ??= createQueryClient());
};

export const api = createTRPCReact<AppRouter>();

export const TRPCReactProvider = ({
  children,
  hideDevTools,
  locale,
}: {
  children: React.ReactNode;
  hideDevTools?: boolean;
  locale?: string;
}) => {
  const queryClient = getQueryClient();

  const linkConfig = {
    url: getBaseUrl() + '/api/trpc',
    headers: () => {
      const headers = new Headers();
      headers.set('x-trpc-source', 'nextjs-react');
      if (locale) {
        headers.set('Accept-Language', locale);
      }
      return headers;
    },
  };

  const [trpcClient] = useState(() =>
    api.createClient({
      links: [
        redirectToLoginOnInvalidToken,
        loggerLink({
          enabled: () => process.env.NODE_ENV === 'development',
        }),

        splitLink({
          // return true to batch the requests
          condition: (_) => process.env.NODE_ENV !== 'development',
          false: httpLink(linkConfig),
          true: httpBatchLink(linkConfig),
        }),
      ],
    }),
  );

  return (
    <QueryClientProvider client={queryClient}>
      <api.Provider client={trpcClient} queryClient={queryClient}>
        {children}
        {!hideDevTools && <ReactQueryDevtools />}
      </api.Provider>
    </QueryClientProvider>
  );
};

const getBaseUrl = () => {
  if (typeof window !== 'undefined') {
    return window.location.origin;
  }
  if (process.env.VERCEL_URL) {
    return `https://${process.env.VERCEL_URL}`;
  }
  return `http://localhost:${process.env.PORT ?? 3000}`;
};
